import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, DialogTitle, IconButton } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * POM-6010 — the fullscreen review's header.
 *
 * Three lines, in the shape the Bulk Update screen established: what this screen is, what to do on
 * it, and the one standing rule that applies the whole time you are here.
 *
 * Close is the only control here. Commit moved down to the filter row, next to the grid whose rows
 * it commits — see BulkLotNdcCommitAction.
 *
 * The type scale below is deliberately literal rather than MUI variants. perfectRxTheme sets
 * MuseoSans at 12px and renders severity=warning as navy, so routing this through theme keys is
 * exactly what made an earlier build of the Bulk Update header miss its approved design. These
 * values mirror `.mhead` in ../styles/bulkOverride.css — if that header's scale changes, this one
 * has to change with it, which is why they are named here instead of scattered inline.
 *
 * The stylesheet itself is not imported. Its own header says it is a one-off that new work should
 * not copy, and it is scoped to that screen's portal; borrowing the look is fine, borrowing the
 * CSS would spread a pattern the codebase has explicitly closed.
 */
const HEAD = {
  title: {
    fontSize: 23,
    fontWeight: 700,
    letterSpacing: '-0.022em',
    lineHeight: 1.15,
    color: '#0F172A',
  },
  suffix: { fontSize: 14, fontWeight: 600, letterSpacing: '-0.004em', color: '#6B7688' },
  subtitle: {
    m: 0,
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '-0.006em',
    lineHeight: 1.3,
    color: '#2B3648',
  },
  meta: { fontSize: 12.5, color: '#4A5568' },
};

const BulkLotNdcReviewHeader = (props) => {
  const { selectedCount, committing, onClose } = props;

  return (
    <DialogTitle
      sx={{
        display: 'flex',
        // flex-start, not center: the block on the left is three lines tall, and Close belongs
        // against the title rather than floating halfway down it.
        alignItems: 'flex-start',
        gap: 1.75,
        px: 3.25,
        pt: 2.75,
        pb: 1.75,
      }}
    >
      {/* minWidth: 0 so a long title wraps instead of pushing the actions off the right edge. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ ...HEAD.title, mb: 1.25 }}>
          Review Lot &amp; NDC
          <Box component='span' sx={{ ...HEAD.suffix, ml: 1 }}>
            {`· ${selectedCount} selected item${selectedCount === 1 ? '' : 's'}`}
          </Box>
        </Box>

        <Box component='h2' sx={HEAD.subtitle}>
          Check each lot and NDC before the labels print
        </Box>

        {/*
          The standing rule, not a status. It is true from the moment this opens until the user
          commits, which is precisely what makes it belong in the header rather than in the bar at
          the bottom — that line changes with every state, and this one does not.

          minHeight holds the row while it is the only thing on it, so nothing below shifts.
        */}
        <Box
          sx={{
            ...HEAD.meta,
            mt: 0.6,
            display: 'flex',
            alignItems: 'center',
            gap: 1.15,
            flexWrap: 'wrap',
            minHeight: 19,
          }}
        >
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              color: '#2B3648',
              fontWeight: 500,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 13, color: '#6B7688' }} />
            Nothing is written until you commit
          </Box>
        </Box>
      </Box>

      <IconButton
        aria-label='Close review'
        onClick={onClose}
        disabled={committing}
        // Matches the Bulk Update header's close affordance: a quiet 30px square that only picks
        // up a background on hover. Leaving the dialog is not the action to advertise here.
        sx={{ width: 30, height: 30, borderRadius: 1.5, color: '#4A5568', flexShrink: 0 }}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
    </DialogTitle>
  );
};

BulkLotNdcReviewHeader.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  committing: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BulkLotNdcReviewHeader;
